import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { 
  Building2, 
  Plus, 
  ArrowRightLeft, 
  Calendar, 
  DollarSign,
  FileText,
  CheckCircle,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES } from "../constants/currencies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BusinessPlanListPage = () => {
  const navigate = useNavigate();
  const { currentPlan, setCurrentPlan, createNewPlan } = useBusinessPlan();
  const { user } = useAuth();
  const [businessPlans, setBusinessPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1,
    subsidiary_id: ""
  });
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchBusinessPlans();
    fetchSubsidiaries();
  }, []);

  const fetchBusinessPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/business-plans`);
      setBusinessPlans(response.data || []);
    } catch (error) {
      console.error("Error fetching business plans:", error);
      toast.error("Failed to load business plans");
      setBusinessPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true for optimized query
      const response = await axios.get(`${BACKEND_URL}/api/enterprise/subsidiaries?company_only=true`);
      setSubsidiaries(response.data || []);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
      setSubsidiaries([]);
    }
  };

  const handleSwitchPlan = async (plan) => {
    try {
      setCurrentPlan(plan);
      toast.success(`Switched to "${plan.name}"`);
      navigate('/dashboard');
    } catch (error) {
      console.error("Error switching plan:", error);
      toast.error("Failed to switch business plan");
    }
  };

  const handleAddNew = () => {
    setFormData({
      name: "",
      company_name: user?.company_name || "",
      currency: "USD",
      fiscal_year_start: 1,
      subsidiary_id: ""
    });
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (subsidiaries.length > 0 && !formData.subsidiary_id) {
      toast.error("Please select a subsidiary for this business plan");
      return;
    }

    setCreateLoading(true);
    try {
      await createNewPlan(formData);
      toast.success("Business plan created successfully!");
      setShowCreateModal(false);
      fetchBusinessPlans();
    } catch (error) {
      console.error("Error creating business plan:", error);
      toast.error("Failed to create business plan");
    } finally {
      setCreateLoading(false);
    }
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-8 overflow-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Business Plan List</h1>
            <p className="text-gray-600 mt-1">
              Manage and switch between your business plans
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={handleAddNew}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New
            </Button>
          </div>
        </div>

        {/* Current Plan Indicator */}
        {currentPlan && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Currently active: <span className="font-semibold">{currentPlan.name}</span>
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading business plans...</p>
              </div>
            </div>
          ) : businessPlans.length === 0 ? (
            /* Empty State */
            <Card className="text-center py-12">
              <CardContent>
                <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Business Plans Yet</h3>
                <p className="text-gray-500 mb-6">
                  Create your first business plan to get started with financial planning.
                </p>
                <Button 
                  onClick={handleAddNew}
                  className="bg-gradient-to-r from-blue-600 to-purple-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Business Plans Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businessPlans.map((plan) => {
                const isActive = currentPlan?.id === plan.id;
                
                return (
                  <Card 
                    key={plan.id} 
                    className={`relative transition-all hover:shadow-lg ${
                      isActive ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''
                    }`}
                  >
                    {isActive && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-blue-600 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <FileText className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg truncate">{plan.name}</CardTitle>
                          <CardDescription className="truncate">
                            {plan.company_name || 'No company name'}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Plan Details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <DollarSign className="h-4 w-4" />
                          <span>Currency: {plan.currency || 'USD'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>Created: {formatDate(plan.created_at)}</span>
                        </div>
                        {plan.updated_at && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="h-4 w-4" />
                            <span>Updated: {formatDate(plan.updated_at)}</span>
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="pt-2">
                        {isActive ? (
                          <Button 
                            variant="outline" 
                            className="w-full" 
                            disabled
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Currently Active
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => handleSwitchPlan(plan)}
                            variant="outline"
                            className="w-full hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                          >
                            <ArrowRightLeft className="h-4 w-4 mr-2" />
                            Switch to This Plan
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {!loading && businessPlans.length > 0 && (
            <div className="mt-8 text-center text-sm text-gray-500">
              Showing {businessPlans.length} business plan{businessPlans.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

      {/* Create Business Plan Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Business Plan</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            {/* Enterprise Subsidiary Selection */}
            {subsidiaries.length > 0 && (
              <div className="space-y-2">
                <Label>Subsidiary/Company *</Label>
                <Select value={formData.subsidiary_id} onValueChange={(value) => 
                  setFormData(prev => ({...prev, subsidiary_id: value}))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subsidiary for this business plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {subsidiaries.map((subsidiary) => (
                      <SelectItem key={subsidiary.id} value={subsidiary.id}>
                        {subsidiary.name} ({subsidiary.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-600">
                  This business plan will be created for the selected subsidiary
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Plan Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                  placeholder="e.g., 2025 Growth Plan"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({...prev, company_name: e.target.value}))}
                  placeholder="e.g., Tech Startup Inc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => 
                  setFormData(prev => ({...prev, currency: value}))
                }>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fiscal Year Start</Label>
                <Select value={formData.fiscal_year_start.toString()} onValueChange={(value) => 
                  setFormData(prev => ({...prev, fiscal_year_start: parseInt(value)}))
                }>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createLoading}>
                {createLoading ? "Creating..." : "Create Business Plan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessPlanListPage;
